import { LitElement, html } from 'lit';
import '../icons/print-icon.js';

/**
 * @component print-upsell-display
 * @category Presentation
 * @description Pure presentation component for print service upsell.
 * Displays specifications, pricing, and action buttons.
 * Does not contain business logic - receives props and emits events.
 *
 * @fires {CustomEvent} add-click - User clicked "Add to booking" button
 * @fires {CustomEvent} contact-click - User clicked "Contact us" button
 *
 * @example
 * <print-upsell-display
 *   volume="4,895"
 *   price="$350.00"
 *   ?loading="${false}"
 *   ?error="${false}"
 *   ?manualQuoteRequired="${false}"
 *   ?adding="${false}"
 *   rmContactName="Aaron Glass"
 *   rmContactEmail="aarong@reach.nz"
 *   quoteReference="TEST123"
 *   @add-click="${handleAddClick}"
 * ></print-upsell-display>
 */
class PrintUpsellDisplay extends LitElement {
    static properties = {
        volume: { type: String },
        price: { type: String },
        loading: { type: Boolean },
        error: { type: Boolean },
        manualQuoteRequired: { type: Boolean },
        adding: { type: Boolean },
        rmContactName: { type: String },
        rmContactEmail: { type: String },
        quoteReference: { type: String },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.volume = '0';
        this.price = null;
        this.loading = false;
        this.error = false;
        this.manualQuoteRequired = false;
        this.adding = false;
        this.rmContactName = 'REACH Team';
        this.rmContactEmail = 'info@reach.nz';
        this.quoteReference = '';
    }

    /**
     * Pre-populated print specifications
     */
    get specs() {
        return {
            paperSize: 'A5',
            pagination: 'Double Sided',
            colours: 'CMYK (Full\u00A0Colour)', // NB: \u00A0 is a non-breaking space
            gsm: '150gsm',
        };
    }

    /**
     * Build mailto URL for contact fallback
     */
    get contactMailtoUrl() {
        const subject = this.manualQuoteRequired
            ? `Custom print quote for ${this.quoteReference} (${this.volume} mailers)`
            : `Print pricing inquiry for quote ${this.quoteReference}`;

        return `mailto:${this.rmContactEmail}?subject=${encodeURIComponent(subject)}`;
    }

    _handleAddClick() {
        this.dispatchEvent(
            new CustomEvent('add-click', {
                bubbles: true,
                composed: true,
            })
        );
    }

    _handleContactClick() {
        this.dispatchEvent(
            new CustomEvent('contact-click', {
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Render loading state for price
     */
    renderPriceLoading() {
        return html`
            <div
                class="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg"
            >
                <span class="text-base font-semibold text-gray-700">Estimated Cost</span>
                <span class="loading loading-spinner loading-md text-blue"></span>
            </div>
        `;
    }

    /**
     * Render successful price display
     */
    renderPriceSuccess() {
        return html`
            <div
                class="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg"
            >
                <span class="text-base font-semibold text-gray-700">Estimated Cost</span>
                <span class="text-2xl font-bold text-peppermint">${this.price}</span>
            </div>
        `;
    }

    /**
     * Render manual quote required message
     */
    renderManualQuoteMessage() {
        return html`
            <div class="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p class="text-base font-medium text-gray-800">
                    This high-volume quote requires a custom pricing assessment.
                </p>
                <p class="text-sm text-gray-600 mt-1">
                    Our team will provide you with the best rate for ${this.volume} mailers.
                </p>
            </div>
        `;
    }

    /**
     * Render pricing error/unavailable message
     */
    renderPriceError() {
        return html`
            <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <p class="text-base font-medium text-gray-800">
                            Unable to calculate pricing automatically.
                        </p>
                        <p class="text-sm text-gray-600 mt-1">
                            Contact us for a quick quote on your print requirements.
                        </p>
                    </div>
                    <a
                        href="${this.contactMailtoUrl}"
                        class="btn btn-outline btn-primary shrink-0 sm:self-center"
                        @click="${this._handleContactClick}"
                    >
                        Contact us for pricing
                    </a>
                </div>
            </div>
        `;
    }

    /**
     * Render the price section based on current state
     */
    renderPriceSection() {
        if (this.loading) {
            return this.renderPriceLoading();
        }

        if (this.manualQuoteRequired) {
            return this.renderManualQuoteMessage();
        }

        if (this.error || !this.price) {
            return this.renderPriceError();
        }

        return this.renderPriceSuccess();
    }

    /**
     * Render the CTA button based on current state
     */
    renderCTA() {
        // Manual quote required - show contact button
        if (this.manualQuoteRequired) {
            return html`
                <a
                    href="${this.contactMailtoUrl}"
                    class="btn btn-primary"
                    @click="${this._handleContactClick}"
                >
                    Contact us for custom quote
                </a>
            `;
        }

        // API error - button is now inside the error message box
        if (this.error || (!this.loading && !this.price)) {
            return html``;
        }

        // Price loaded - show add button and contact option
        if (this.price && !this.loading) {
            return html`
                <a
                    href="${this.contactMailtoUrl}"
                    class="btn btn-outline"
                    @click="${this._handleContactClick}"
                >
                    Contact us for other options
                </a>
                <button
                    class="btn btn-primary"
                    ?disabled="${this.adding}"
                    @click="${this._handleAddClick}"
                >
                    ${this.adding
                        ? html`<span class="loading loading-spinner loading-sm mr-2"></span>`
                        : ''}
                    ${this.adding ? 'Adding...' : 'Add to booking'}
                </button>
            `;
        }

        // Loading - show disabled button
        return html`
            <button class="btn btn-primary" disabled>
                <span class="loading loading-spinner loading-sm mr-2"></span>
                Loading price...
            </button>
        `;
    }

    renderCardContent() {
        return html`
            <div class="card-body p-6">
                <!-- Header -->
                <div class="flex flex-row items-start gap-3 mb-4">
                    <print-icon
                        class="shrink-0 text-peppermint"
                        aria-label="Print services"
                    ></print-icon>
                    <div class="flex-1">
                        <h3 class="text-lg font-bold text-gray-900">Add Print Services</h3>
                        <p class="text-sm text-gray-600 mt-1">
                            Complete your letterbox campaign with professionally printed mailers
                        </p>
                    </div>
                </div>

                <!-- Specifications Grid -->
                <div class="bg-peppermint/15 border border-peppermint/30 rounded-lg p-4 mb-4">
                    <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div>
                            <p class="field-label">Paper Size</p>
                            <p class="text-base font-medium text-gray-900">
                                ${this.specs.paperSize}
                            </p>
                        </div>
                        <div>
                            <p class="field-label">Pagination</p>
                            <p class="text-base font-medium text-gray-900">
                                ${this.specs.pagination}
                            </p>
                        </div>
                        <div>
                            <p class="field-label">Colours</p>
                            <p class="text-base font-medium text-gray-900">${this.specs.colours}</p>
                        </div>
                        <div>
                            <p class="field-label">Paper Weight</p>
                            <p class="text-base font-medium text-gray-900">${this.specs.gsm}</p>
                        </div>
                        <div>
                            <p class="field-label">Volume</p>
                            <p class="text-base font-medium text-gray-900">
                                ${this.volume} mailers
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Price Display -->
                ${this.renderPriceSection()}

                <!-- CTA Button -->
                <div class="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
                    ${this.renderCTA()}
                </div>
            </div>
        `;
    }

    render() {
        return html`
            <div class="relative">
                <!-- Scissors icon on top border -->
                <div class="absolute top-0 -translate-y-1/2 left-8 px-1 z-10">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="w-7 h-7 text-peppermint"
                    >
                        <circle cx="6" cy="6" r="3"></circle>
                        <circle cx="6" cy="18" r="3"></circle>
                        <line x1="20" y1="4" x2="8.12" y2="15.88"></line>
                        <line x1="14.47" y1="14.48" x2="20" y2="20"></line>
                        <line x1="8.12" y1="8.12" x2="12" y2="12"></line>
                    </svg>
                </div>
                <div
                    class="card-standard border-2 border-dashed border-peppermint bg-peppermint/5 mb-8"
                >
                    ${this.renderCardContent()}
                </div>
            </div>
        `;
    }
}

customElements.define('print-upsell-display', PrintUpsellDisplay);
